<script setup lang="ts">
    import DraggableWindow from '@/components/window/DraggableWindow.vue';
    import { useWindowManager } from '@/composables/useWindowManager';

    const route = useRoute();
    const { windows, activeWindowId, closeWindow, closeAllWindows, updateWindowPosition, updateWindowSize, activateWindow, minimizeWindow, toggleFullscreen } =
        useWindowManager();

    watch(
        () => route.path,
        () => {
            if (windows.value.length > 0) {
                closeAllWindows();
            }
        }
    );

    const handleCloseWindow = (windowId: string) => {
        closeWindow(windowId);
    };

    const handleUpdatePosition = (windowId: string, x: number, y: number) => {
        updateWindowPosition(windowId, x, y);
    };

    const handleUpdateSize = (windowId: string, w: number, h: number) => {
        updateWindowSize(windowId, w, h);
    };

    const handleActivateWindow = (windowId: string) => {
        activateWindow(windowId);
    };

    const handleToggleFullscreen = (windowId: string) => {
        toggleFullscreen(windowId);
    };

    const handleMinimizeWindow = (windowId: string) => {
        minimizeWindow(windowId);
    };

    const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && activeWindowId.value) {
            event.preventDefault();
            event.stopPropagation();
            closeWindow(activeWindowId.value);
        }
    };

    onMounted(() => {
        window.addEventListener('keydown', handleEscKey);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('keydown', handleEscKey);
    });
</script>

<template>
    <DraggableWindow
        v-for="window in windows"
        :key="window.id"
        :window-id="window.id"
        :initial-x="window.x"
        :initial-y="window.y"
        :initial-w="window.w"
        :initial-h="window.h"
        :z-index="window.zIndex"
        :active="activeWindowId === window.id"
        :is-fullscreen="window.isFullscreen"
        :is-minimized="window.isMinimized"
        :header-title="window.headerTitle"
        :header-icon="window.headerIcon"
        :show-fullscreen-button="window.showFullscreenButton"
        :show-filters-button="window.showFiltersButton"
        :show-json-toggle-button="window.showJsonToggleButton"
        :on-fullscreen-toggle="() => handleToggleFullscreen(window.id)"
        @close="handleCloseWindow(window.id)"
        @minimize="handleMinimizeWindow(window.id)"
        @update:position="(x, y) => handleUpdatePosition(window.id, x, y)"
        @update:size="(w, h) => handleUpdateSize(window.id, w, h)"
        @activate="handleActivateWindow(window.id)"
    >
        <component
            :is="window.component"
            v-bind="{
                ...window.data,
                windowId: window.id,
            }"
        />
    </DraggableWindow>
</template>
